import 'src/screen/Protected/ApiKeysScreen/ApiKeysScreen.scss';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Modal, Paper, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Box, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Button from 'src/component/UI/Button';
import ApiKeysList from './ApiKeysList';
import { createApiKey, clearApiKeyToken, updateApiKey } from 'src/module/apiKeys/action';
import { getApiKeyCreationLoading, getNewApiKeyToken } from 'src/module/apiKeys/selector';
import toast from 'src/utils/toast';
import { getPermissions } from 'src/module/permission/selector';
import { fetchPermissions } from 'src/module/permission/action';
import { rolesNameToKey } from 'src/utils/utils';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';

// Helper function to convert role names back to keys (same mapping as rolesNameToKey)
const roleNameToKey = (roleName: string): string => {
  return rolesNameToKey(roleName);
};

// Mapping from role names to translation keys
const roleNameToTranslationKey: Record<string, string> = {
  'Administrator': 'users.administrator',
  'Regular User': 'users.regularUser',
  'Maintenance User': 'users.maintenanceUser',
  'EHT Specialist': 'users.ehtSpecialist',
  'Operations': 'users.operations',
};


export default function ApiKeysScreen () {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Helper to get translated role label
  const getRoleLabel = (roleName: string): string => {
    const translationKey = roleNameToTranslationKey[roleName];
    return translationKey ? t(translationKey) : roleName;
  };

  // Helper to get translated role label from role key
  const getRoleLabelFromKey = (roleKey: string, rolesList: string[]): string => {
    const roleName = rolesList.find(role => rolesNameToKey(role) === roleKey);
    return roleName ? getRoleLabel(roleName) : roleKey;
  };
  const [isAddApiKeyModalOpen, setIsAddApiKeyModalOpen] = useState(false);
  const [isEditApiKeyModalOpen, setIsEditApiKeyModalOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [apiKeyName, setApiKeyName] = useState('');
  const [editingApiKey, setEditingApiKey] = useState(null);
  const [editApiKeyName, setEditApiKeyName] = useState('');
  const [editSelectedRoles, setEditSelectedRoles] = useState([]);
  const permissions = useSelector(getPermissions);

  const creationLoading = useSelector(getApiKeyCreationLoading);
  const newApiKeyToken = useSelector(getNewApiKeyToken);
  const [availableRoles, setAvailableRoles] = useState([]);

  useEffect(() => {
    dispatch(fetchPermissions());
  }, []);

  useEffect(() => {
    if (permissions) {
      const allRoles = [];
      permissions.map((perm) => {
        const roles = perm.get('role');
        roles?.map(role => {
          if (!allRoles.includes(role) && role != 'SmartTrace Admin' && role != 'Super Admin') {
            allRoles.push(role);
          }
        });
      });
      setAvailableRoles(allRoles);
    }
  }, [permissions]);

  const handleCreateApiKey = () => {
    dispatch(createApiKey(selectedRoles, apiKeyName || null));
  };

  const handleCloseModal = () => {
    setIsAddApiKeyModalOpen(false);
    setSelectedRoles([]);
    setApiKeyName('');
    // Clear the token from the store when modal is closed
    if (newApiKeyToken) {
      dispatch(clearApiKeyToken());
    }
  };

  const handleCopyToken = () => {
    if (newApiKeyToken) {
      navigator.clipboard.writeText(newApiKeyToken);
      toast.success(t('toast.apiKeyCopied'));
    }
  };

  const handleRoleChange = (event) => {
    setSelectedRoles(event.target.value);
  };

  const handleEditRoleChange = (event) => {
    setEditSelectedRoles(event.target.value);
  };

  const getRoleKeys = (permissionLevel: string) => {
    // Convert permission_level (comma-separated role names) to role keys
    const roleKeys: string[] = [];
    if (permissionLevel) {
      const roleNames = permissionLevel.split(',').map((r: string) => r.trim());
      roleNames.forEach((roleName: string) => {
        const roleKey = roleNameToKey(roleName);
        if (roleKey && availableRoles.includes(roleName)) {
          roleKeys.push(roleKey);
        }
      });
    }
    return roleKeys;
  };

  useEffect(() => {
    if (editingApiKey) {
      const permissionLevel = editingApiKey.get('permission_level') || '';
      const roleKeys = getRoleKeys(permissionLevel);
      setEditSelectedRoles(roleKeys);
      setEditApiKeyName(editingApiKey.get('name') || '');
    }
  }, [editingApiKey]);

  const handleEditApiKey = (apiKey: any) => {
    setEditingApiKey(apiKey);
    setIsEditApiKeyModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditApiKeyModalOpen(false);
    setEditingApiKey(null);
    setEditApiKeyName('');
    setEditSelectedRoles([]);
  };

  const handleUpdateApiKey = () => {
    if (editingApiKey) {
      dispatch(updateApiKey(editingApiKey.get('id'), editApiKeyName || null, editSelectedRoles));
      handleCloseEditModal();
    }
  };

  return (
    <div id='api-keys-screen' className='api-keys-screen page'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper>
            <h2 >
              {t('apiKeys.manageApiKeys')}
            </h2>
            <div className='inner-section'>
              <div className='select-device-bar'>
              </div>
              <div className='api-keys-list-container'>
                <ApiKeysList onEdit={handleEditApiKey} />
              </div>
              <div className='button-bar'>
                <Button cta backgroundColor='primary'
                  icon={<AddIcon />}
                  onClick={() => setIsAddApiKeyModalOpen(true)}
                >
                  {t('apiKeys.addApiKey')}
                </Button>
              </div>
            </div>
          </Paper>
        </Grid>
      </Grid>
      <Modal
        open={isAddApiKeyModalOpen}
        onClose={handleCloseModal}
        aria-labelledby='api-key-modal-title'
      >
        <div className='modal api-keys-modal' style={{ minWidth: '500px', minHeight: '250px' }}>
          <Typography variant="h4" component="h2" gutterBottom>
            {t('apiKeys.createApiKey')}
          </Typography>

          {!newApiKeyToken ? (
            <>
              <Box mt={3} mb={3}>
                <TextField
                  fullWidth
                  label={t('apiKeys.apiKeyNameOptional')}
                  value={apiKeyName}
                  onChange={(e) => setApiKeyName(e.target.value)}
                  variant="outlined"
                  margin="normal"
                />
              </Box>
              <Box mt={3} mb={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('apiKeys.selectRolesOptional')}</InputLabel>
                  <Select
                    multiple
                    value={selectedRoles}
                    onChange={handleRoleChange}
                    renderValue={(selected) => (
                      <Box className='role-chips-container'>
                        {selected.map((value) => (
                          <Chip key={value} label={getRoleLabelFromKey(value, availableRoles)} size="small" style={{ fontSize: '1.3rem' }} />
                        ))}
                      </Box>
                    )}
                  >
                    {availableRoles.map((role) => {
                      return (
                        <MenuItem key={role} value={rolesNameToKey(role)}>
                          {getRoleLabel(role)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  onClick={handleCloseModal}
                  disabled={creationLoading}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  cta
                  backgroundColor="primary"
                  onClick={handleCreateApiKey}
                  disabled={creationLoading}
                >
                  {creationLoading ? t('apiKeys.generating') : t('apiKeys.generateApiKey')}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                gutterBottom
                className='warning-text'
                style={{ color: variables.error, textAlign: 'center' }}
              >
                {t('apiKeys.copyKeyWarning')}
              </Typography>

              <Box mt={2} mb={3}>
                <TextField
                  fullWidth
                  label={t('apiKeys.yourNewApiKey')}
                  value={newApiKeyToken}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={handleCopyToken} size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    ),
                  }}
                  variant="outlined"
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  cta
                  backgroundColor="primary"
                  onClick={handleCloseModal}
                >
                  {t('apiKeys.done')}
                </Button>
              </Box>
            </>
          )}
        </div>
      </Modal>
      <Modal
        open={isEditApiKeyModalOpen}
        onClose={handleCloseEditModal}
        aria-labelledby='edit-api-key-modal-title'
      >
        <div className='modal api-keys-modal' style={{ minWidth: '500px', minHeight: '250px' }}>
          <Typography variant="h4" component="h2" gutterBottom>
            {t('apiKeys.editApiKey')}
          </Typography>

          <Box mt={3} mb={3}>
            <TextField
              fullWidth
              label={t('apiKeys.apiKeyNameOptional')}
              value={editApiKeyName}
              onChange={(e) => setEditApiKeyName(e.target.value)}
              variant="outlined"
              margin="normal"
            />
          </Box>
          <Box mt={3} mb={3}>
            <FormControl fullWidth>
              <InputLabel>{t('apiKeys.selectRolesOptional')}</InputLabel>
              <Select
                multiple
                value={editSelectedRoles}
                onChange={handleEditRoleChange}
                renderValue={(selected) => (
                  <Box className='role-chips-container'>
                    {selected.map((value) => (
                      <Chip key={value} label={getRoleLabelFromKey(value, availableRoles)} size="small" style={{ fontSize: '1.3rem' }} />
                    ))}
                  </Box>
                )}
              >
                {availableRoles.map((role) => {
                  return (
                    <MenuItem key={role} value={rolesNameToKey(role)}>
                      {getRoleLabel(role)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              onClick={handleCloseEditModal}
            >
              {t('common.cancel')}
            </Button>
            <Button
              cta
              backgroundColor="primary"
              onClick={handleUpdateApiKey}
            >
              {t('apiKeys.saveChanges')}
            </Button>
          </Box>
        </div>
      </Modal>
    </div>
  );
}
